import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, Mail, Phone, MapPin, KeyRound } from 'lucide-react';

const initialFormState = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  postal_code: '',
  country: '',
  role: 'customer',
  is_active: true,
  email_verified: false
};

const EditUserModal = ({ isOpen, onClose, user, onSave, saving, onResetPassword, onDelete }) => {
  const [form, setForm] = useState(initialFormState);

  useEffect(() => {
    if (user) {
      setForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        postal_code: user.postal_code || '',
        country: user.country || '',
        role: user.role || 'customer',
        is_active: user.is_active === undefined ? true : Boolean(user.is_active),
        email_verified: Boolean(user.email_verified)
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleToggle = (name) => {
    setForm(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSave) onSave(form);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 transition-opacity z-[9998]"
            aria-hidden="true"
            onClick={() => onClose && onClose('cancel')}
          >
            <div className="absolute inset-0 bg-gray-900 opacity-75"></div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative z-[9999] inline-block align-bottom bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full border border-gray-700"
          >
            <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">Modifier l'utilisateur</h3>
              <button onClick={() => onClose && onClose('cancel')} className="text-gray-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Prénom</label>
                  <input name="first_name" value={form.first_name} onChange={handleChange} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Nom</label>
                  <input name="last_name" value={form.last_name} onChange={handleChange} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm text-gray-300 mb-1">Email</label>
                  <div className="relative">
                    <Mail className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input type="email" name="email" value={form.email} onChange={handleChange} className="w-full pl-9 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm text-gray-300 mb-2">Rôle</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: 'customer', label: 'Client' },
                      { key: 'staff', label: 'Staff' },
                      { key: 'admin', label: 'Admin' }
                    ].map(opt => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, role: opt.key }))}
                        className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors whitespace-nowrap ${
                          form.role === opt.key
                            ? 'bg-yellow-600 border-yellow-500 text-white'
                            : 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                        }`}
                      >
                        Définir {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Téléphone</label>
                  <div className="relative">
                    <Phone className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input name="phone" value={form.phone} onChange={handleChange} className="w-full pl-9 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Pays</label>
                  <div className="relative">
                    <MapPin className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input name="country" value={form.country} onChange={handleChange} className="w-full pl-9 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm text-gray-300 mb-1">Adresse</label>
                  <input name="address" value={form.address} onChange={handleChange} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Ville</label>
                  <input name="city" value={form.city} onChange={handleChange} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Code postal</label>
                  <input name="postal_code" value={form.postal_code} onChange={handleChange} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="inline-flex items-center gap-2 text-sm text-gray-200">
                  <input type="checkbox" checked={form.is_active} onChange={() => handleToggle('is_active')} className="form-checkbox h-4 w-4 text-yellow-500" />
                  Actif
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-gray-200">
                  <input type="checkbox" checked={form.email_verified} onChange={() => handleToggle('email_verified')} className="form-checkbox h-4 w-4 text-yellow-500" />
                  Email vérifié
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <button type="button" onClick={() => onDelete && onDelete(user)} className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg inline-flex items-center justify-center gap-2 whitespace-nowrap">
                  Supprimer l'utilisateur
                </button>
                <button type="button" onClick={() => onResetPassword && onResetPassword(user)} className="w-full px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg inline-flex items-center justify-center gap-2 whitespace-nowrap">
                  <KeyRound className="h-4 w-4" />
                  Réinitialiser mot de passe
                </button>
                <button type="button" onClick={() => onClose && onClose('cancel')} className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg whitespace-nowrap">
                  Annuler
                </button>
                <button type="submit" disabled={saving} className="w-full px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg inline-flex items-center justify-center gap-2 disabled:opacity-60 whitespace-nowrap">
                  <Save className="h-4 w-4" />
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default EditUserModal;


